import { Link } from "@inertiajs/react"
import { Section as ContentSection } from "@/components/legal/Section"
import { MarketingFooter } from "@/components/marketing-footer"



import { useFlashcardProps } from "@/hooks/use-flashcard-props"

interface PrivacyPolicyProps {
  lastUpdated?: string
  canonicalUrl?: string
}

export default function PrivacyPolicy(props: any) {
  const { lastUpdated } =
    useFlashcardProps<PrivacyPolicyProps>(props)

  return (
    <>


      {/* Hero Section */}
      <div className="border-b bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="mb-6">
            <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Link
                href="/"
                className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                Home
              </Link>
              <span>/</span>
              <span className="text-foreground font-medium">Privacy Policy</span>
            </nav>
          </div>

          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              How ThaiCopilot collects, uses, and protects your personal data. We're committed to transparency and protecting your privacy.
            </p>
            {lastUpdated && (
              <div className="mt-6">
                <span className="inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap border-slate-200 dark:border-slate-800 text-foreground">
                  Last updated: {lastUpdated}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen bg-white dark:bg-slate-950">
        <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
          <article className="prose prose-slate dark:prose-invert max-w-none prose-headings:font-bold prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4 prose-p:text-base prose-p:leading-relaxed prose-p:text-slate-700 dark:prose-p:text-slate-300 prose-li:text-base prose-li:leading-relaxed prose-a:text-indigo-600 dark:prose-a:text-indigo-400 prose-a:no-underline hover:prose-a:underline prose-strong:text-slate-900 dark:prose-strong:text-slate-100 prose-strong:font-semibold">
            <ContentSection id="introduction" title="Introduction">
              <p>
                Welcome to ThaiCopilot. We respect your privacy and are committed to
                protecting your personal data. This privacy policy explains how we
                collect, use, store, and protect your information when you use our
                Thai language learning platform.
              </p>
              <p>
                ThaiCopilot is operated by Rahul Roy, a solo founder based in India who moved to Thailand 2 years back.
                We&#39;re building this platform to help expats and travelers learn Thai
                effectively through AI-powered conversations and flashcards.
              </p>
              <p>
                This policy complies with Thailand&#39;s PDPA (Personal Data Protection
                Act), India&#39;s Personal Data Protection Act, and the EU&#39;s GDPR
                where applicable.
              </p>
            </ContentSection>

            <ContentSection id="data-collection" title="What Data We Collect">
              <p>We collect minimal data necessary to provide our service:</p>

              <h3>Account Information:</h3>
              <ul>
                <li>
                  <strong>Email address</strong> - For account creation, login, and
                  essential communications
                </li>
                <li>
                  <strong>Name</strong> - To personalize your experience
                </li>
                <li>
                  <strong>Password</strong> - Securely hashed, never stored in plain
                  text
                </li>
              </ul>

              <h3>Learning Data:</h3>
              <ul>
                <li>
                  <strong>Conversations</strong> - Messages you send and receive while
                  practicing Thai
                </li>
                <li>
                  <strong>Flashcards</strong> - Cards you create, practice results,
                  and progress data
                </li>
                <li>
                  <strong>Usage patterns</strong> - Features you use, session
                  duration, learning streaks
                </li>
              </ul>

              <h3>Technical Data:</h3>
              <ul>
                <li>
                  <strong>IP address</strong> - For security and fraud prevention
                  (anonymized in analytics)
                </li>
                <li>
                  <strong>Browser & device information</strong> - To optimize our
                  platform
                </li>
                <li>
                  <strong>Session cookies</strong> - To keep you logged in
                </li>
              </ul>

              <p>
                <strong>We do not collect:</strong> Payment information (when paid
                features launch, we&#39;ll use trusted payment processors), location data
                beyond country-level analytics, or any sensitive personal information
                beyond what&#39;s listed above.
              </p>
            </ContentSection>

            <ContentSection id="data-usage" title="How We Use Your Data">
              <p>We use your data exclusively for the following purposes:</p>

              <ol>
                <li>
                  <strong>Service Delivery:</strong> To provide Thai language learning
                  features, save your progress, and personalize your experience
                </li>
                <li>
                  <strong>Account Management:</strong> To create and maintain your
                  account, authenticate logins, and send essential service emails
                  (verification, password resets)
                </li>
                <li>
                  <strong>Platform Improvement:</strong> To understand how users
                  interact with features, identify bugs, and improve the learning
                  experience
                </li>
                <li>
                  <strong>Security:</strong> To detect and prevent fraud, abuse, and
                  unauthorized access
                </li>
                <li>
                  <strong>Legal Compliance:</strong> To comply with legal obligations
                  and enforce our Terms of Service
                </li>
              </ol>

              <p>
                <strong>What we don&#39;t do:</strong> We do not sell your data to third
                parties. We do not send marketing emails (only transactional emails).
                We do not use your conversations for advertising.
              </p>
            </ContentSection>

            <ContentSection id="data-storage" title="Data Storage & Security">
              <h3>Current Practice:</h3>
              <p>
                Your data is currently stored privately on our secure servers. All
                data is encrypted in transit using HTTPS/SSL and at rest using
                industry-standard encryption. Passwords are hashed using bcrypt, a
                secure one-way hashing algorithm.
              </p>

              <h3>Security Measures:</h3>
              <ul>
                <li>
                  <strong>Encryption:</strong> All connections use HTTPS/SSL
                  encryption
                </li>
                <li>
                  <strong>Password Security:</strong> Bcrypt hashing with secure salts
                </li>
                <li>
                  <strong>Access Control:</strong> Strict access controls and
                  authentication
                </li>
                <li>
                  <strong>CloudFlare Protection:</strong> DDoS protection and bot
                  filtering
                </li>
                <li>
                  <strong>Regular Monitoring:</strong> We follow Rails security
                  guidelines and monitor for vulnerabilities
                </li>
              </ul>

              <h3>Future Changes:</h3>
              <p>
                We may use anonymized or aggregated data (not linked to individuals)
                to improve the platform. For example, &quot;Users struggle with tone marks&quot;
                helps us improve lessons. We&#39;ll provide 14 days notice before any
                material changes to how we use your data.
              </p>

              <h3>Data Retention:</h3>
              <p>
                We retain your data until you delete your account or request deletion.
                After deletion, we&#39;ll remove your personal data within 30 days, except
                where required by law.
              </p>
            </ContentSection>

            <ContentSection id="third-party" title="Third-Party Services">
              <p>
                We use the following third-party services to operate ThaiCopilot.
                These services may process your data:
              </p>

              <h3>Analytics & Monitoring:</h3>
              <ul>
                <li>
                  <strong>Google Analytics 4:</strong> Usage analytics and page views
                  (IP anonymization enabled).
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <strong>Mixpanel:</strong> Product analytics and feature usage
                  tracking.
                  <a
                    href="https://mixpanel.com/legal/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <strong>New Relic:</strong> Performance monitoring and error
                  tracking.
                  <a
                    href="https://newrelic.com/termsandconditions/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <strong>Custom Analytics:</strong> We may implement our own
                  analytics system to better understand user behavior
                </li>
              </ul>

              <h3>International Data Transfers:</h3>
              <p>
                These third-party services may transfer and process data in the United
                States or other countries. We ensure appropriate safeguards are in
                place through standard contractual clauses and service agreements.
              </p>

              <h3>Your Control:</h3>
              <p>
                You can decline analytics cookies through your browser settings. Note
                that this may affect some non-essential features. We may add or change
                analytics providers with 14 days notice.
              </p>
            </ContentSection>

            <ContentSection id="ai-processing" title="AI Processing">
              <h3>Current Practice:</h3>
              <p>
                Your conversation data is currently processed in-house on our servers.
                We do not send your messages to third-party AI providers.
              </p>

              <h3>Future Changes:</h3>
              <p>
                We may use third-party AI services (such as OpenAI or Anthropic) in
                the future to improve conversation quality and language learning
                features. Before making this change:
              </p>
              <ul>
                <li>We&#39;ll provide 14 days advance notice</li>
                <li>We&#39;ll update this policy with specific details</li>
                <li>We&#39;ll ensure providers have strong privacy protections</li>
                <li>You&#39;ll have the option to delete your data if you disagree</li>
              </ul>
            </ContentSection>

            <ContentSection id="user-rights" title="Your Rights">
              <p>
                Under PDPA, GDPR, and Indian data protection laws, you have the
                following rights regarding your personal data:
              </p>

              <h3>Right to Access:</h3>
              <p>
                You can request a copy of all personal data we hold about you. React out via contact us form.
              </p>

              <h3>Right to Deletion:</h3>
              <p>
                You can delete your account and all associated data at any time
                through Settings → Profile → Delete Account. We&#39;ll permanently remove
                your data within 30 days.
              </p>

              <h3>Right to Portability:</h3>
              <p>
                You can export your learning data (conversations, flashcards,
                progress) in JSON format. This feature is coming soon to the
                dashboard.
              </p>

              <h3>Right to Correction:</h3>
              <p>
                You can update your name and email through Settings → Profile. For
                other corrections, contact us.
              </p>

              <h3>Right to Object:</h3>
              <p>
                You can object to data processing for specific purposes. Note that
                this may limit platform functionality.
              </p>

              <h3>How to Exercise Your Rights:</h3>
              <p>
                <Link
                  href="/contact"
                  className="text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  Contact us
                </Link>{" "}
                with your request. We&#39;ll respond within 30 days.
              </p>
            </ContentSection>

            <ContentSection id="cookies" title="Cookies & Analytics">
              <h3>Essential Cookies:</h3>
              <p>
                We use session cookies to keep you logged in. These are required for
                the platform to function and cannot be disabled.
              </p>

              <h3>Analytics Cookies (Third-Party):</h3>
              <ul>
                <li>Google Analytics 4: Usage analytics, page views</li>
                <li>Mixpanel: Product analytics, feature usage tracking</li>
                <li>New Relic: Performance monitoring</li>
              </ul>

              <h3>Your Control:</h3>
              <p>You can decline analytics cookies through your browser settings:</p>
              <ul>
                <li>
                  <strong>Chrome:</strong> Settings → Privacy and Security → Cookies
                </li>
                <li>
                  <strong>Firefox:</strong> Settings → Privacy & Security → Cookies
                  and Site Data
                </li>
                <li>
                  <strong>Safari:</strong> Preferences → Privacy → Cookies and website
                  data
                </li>
              </ul>
              <p>
                Declining analytics cookies won&#39;t affect core learning features, but
                may limit our ability to improve the platform based on usage patterns.
              </p>
            </ContentSection>

            <ContentSection id="changes" title="Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time to reflect changes
                in our practices or legal requirements.
              </p>

              <h3>How We&#39;ll Notify You:</h3>
              <ul>
                <li>
                  We&#39;ll email you at least 14 days before material changes take effect
                </li>
                <li>We&#39;ll update the &quot;Last updated&quot; date at the top of this page</li>
                <li>
                  For minor clarifications, we&#39;ll update the policy without advance
                  notice
                </li>
              </ul>

              <h3>Your Options:</h3>
              <p>
                If you disagree with changes, you can delete your account before they
                take effect. Continued use of ThaiCopilot after changes means you
                accept the updated policy.
              </p>
            </ContentSection>

            <ContentSection id="contact" title="Contact Us">
              <p>
                If you have questions, concerns, or requests regarding this Privacy
                Policy or your personal data, please contact us:
              </p>

              <div className="not-prose space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-6 dark:border-slate-800 dark:bg-slate-900">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  ThaiCopilot Privacy Team
                </p>
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  <Link
                    href="/contact"
                    className="text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    Contact Us
                  </Link>
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  We aim to respond to all inquiries within 24-48 hours.
                </p>
              </div>

              <p className="mt-6">
                For data protection complaints in your jurisdiction:
              </p>
              <ul>
                <li>
                  <strong>India:</strong> Ministry of Electronics and Information
                  Technology
                </li>
                <li>
                  <strong>Thailand:</strong> Personal Data Protection Commission
                  (PDPC)
                </li>
                <li>
                  <strong>EU:</strong> Your local data protection authority
                </li>
              </ul>
            </ContentSection>
          </article>
        </div>
      </div>

      <MarketingFooter />
    </>
  )
}
